import { useEffect, useRef, useState } from "react";
import {
	Alert,
	Box,
	Checkbox,
	CircularProgress,
	DialogContent,
	Divider,
	List,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	Paper,
	Stack,
	Typography,
} from "@mui/material";
import { PictureAsPdfOutlined, PrintOutlined, ReceiptOutlined } from "@mui/icons-material";
import {
	MoleCancelButton,
	MoleDialog,
	MoleDialogActions,
	MoleDialogHeader,
	MolePrimaryButton,
	useMoleThemeColor,
} from "./MoleDialogShell";
import { useAuth } from "../hooks/useAuth";
import { useUI } from "../providers/MoleUIContext";
import { Z_INDEX } from "../styles/zIndex";
import { Util } from "../util/Util";
import { fetchReceiptPreview, issueReceipt, ReceiptFormat, ReceiptSource } from "../util/receiptPdf";

interface Props {
	/** What to print; null keeps the dialog closed. */
	source: ReceiptSource | null;
	onClose: () => void;
	/** Optional line above the receipt, e.g. after a payment was saved. */
	message?: string;
}

interface PreviewPayment {
	id: number;
	date: string;
	amount: number;
	is_instant: boolean;
	mode: string;
	user: string;
	description?: string | null;
}

interface PreviewInvoice {
	invoice: string;
	reference: string;
	invoice_total: number;
	paid_to_date: number;
	balance: number;
	subtotal: number;
	payments: PreviewPayment[];
}

interface PreviewDetails {
	client: string;
	contact_name?: string | null;
	contact_lines?: string[];
}

interface PaymentPreview {
	business: string;
	currency: string;
	receipt_no: string;
	date: string;
	details: PreviewDetails;
	invoices: PreviewInvoice[];
	total_received: number;
	total_balance: number;
	total_words: string;
}

interface WalletPreview {
	business: string;
	currency: string;
	receipt_no: string;
	date: string;
	details: PreviewDetails;
	record: {
		tag: string;
		mode?: string | null;
		ref?: string | null;
		description?: string | null;
		user: string;
		source_client_name?: string | null;
	};
	amount: number;
	balance: number;
	total_words: string;
}

type Preview = PaymentPreview | WalletPreview;

const isWalletPreview = (preview: Preview): preview is WalletPreview => "record" in preview;

const money = (value: number, currency: string) =>
	`${Util.formatNumber(Number(value || 0))}${currency ? ` ${currency}` : ""}`;

/**
 * Show the receipt on screen first — ready to screenshot and send — then
 * print it on the receipt printer or open it as a PDF. For an invoice the
 * payments can be ticked off below the receipt; the receipt follows the ticks.
 */
const ReceiptDialog = ({ source, onClose, message }: Props) => {
	const { api } = useAuth();
	const { t, setLoadingMessage } = useUI();
	const themeColor = useMoleThemeColor();

	const [preview, setPreview] = useState<Preview | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);
	// Invoice mode: every receiptable payment on the invoice, and the ticked ones.
	const [invoicePayments, setInvoicePayments] = useState<PreviewPayment[]>([]);
	const [picked, setPicked] = useState<Set<number>>(new Set());
	const fullPreview = useRef<Preview | null>(null);
	const requestId = useRef(0);

	const load = async (target: ReceiptSource) => {
		const id = ++requestId.current;
		setLoading(true);
		setError(null);
		const res = await fetchReceiptPreview(api, target);
		if (id !== requestId.current) return null;
		setLoading(false);
		if (res?.status === 200 && res.data) {
			setPreview(res.data);
			return res.data as Preview;
		}
		setPreview(null);
		setError(res?.data?.message || t("common:report.failedToGenerate"));
		return null;
	};

	useEffect(() => {
		setPreview(null);
		setError(null);
		setInvoicePayments([]);
		setPicked(new Set());
		fullPreview.current = null;
		if (!source) return;
		void load(source).then((data) => {
			if (source.kind !== "invoice" || !data || isWalletPreview(data)) return;
			fullPreview.current = data;
			const rows = data.invoices.flatMap((inv) => inv.payments);
			setInvoicePayments(rows);
			setPicked(new Set(rows.map((row) => row.id)));
		});
	}, [source]);

	if (!source) return null;

	// Every payment ticked: ask for the invoice itself so the history is complete.
	const effectiveSource = (ids: Set<number>): ReceiptSource | null => {
		if (source.kind !== "invoice") return source;
		if (ids.size === 0) return null;
		if (ids.size === invoicePayments.length) return source;
		return { kind: "payments", ids: Array.from(ids) };
	};

	const toggle = (id: number) => {
		const next = new Set(picked);
		if (next.has(id)) next.delete(id);
		else next.add(id);
		setPicked(next);
		const target = effectiveSource(next);
		if (!target) {
			requestId.current++;
			setPreview(null);
			return;
		}
		if (target === source && fullPreview.current) {
			requestId.current++;
			setPreview(fullPreview.current);
			setError(null);
			return;
		}
		void load(target);
	};

	const run = async (format: ReceiptFormat) => {
		const target = effectiveSource(picked);
		if (!target) return;
		const ok = await issueReceipt(api, target, format, setLoadingMessage);
		if (ok) onClose();
	};

	const isWallet = source.kind === "wallet";
	const disabled = loading || !preview;

	return (
		<MoleDialog
			open
			onClose={onClose}
			maxWidth="xs"
			fullWidth
			themeColor={themeColor}
			paperOptions={{ maxHeight: "95vh" }}
			sx={{ zIndex: Z_INDEX.nestedDialog }}
		>
			<MoleDialogHeader
				title={isWallet ? t("common:receipt.walletTitle") : t("common:receipt.paymentTitle")}
				icon={<ReceiptOutlined />}
				onClose={onClose}
				closeLabel={t("common:ui.buttons.cancel")}
				themeColor={themeColor}
			/>
			<DialogContent sx={{ pt: 1, pb: 2 }}>
				{message && (
					<Typography variant="body2" sx={{ mb: 1.5 }}>
						{message}
					</Typography>
				)}

				{error && <Alert severity="warning" sx={{ mb: 1.5 }}>{error}</Alert>}

				{!preview && !error && loading && (
					<Stack alignItems="center" sx={{ py: 4 }}>
						<CircularProgress size={28} sx={{ color: themeColor }} />
					</Stack>
				)}

				{preview && (
					<Box sx={{ opacity: loading ? 0.5 : 1, transition: "opacity 120ms" }}>
						{isWalletPreview(preview) ? <WalletCard preview={preview} /> : <PaymentCard preview={preview} />}
					</Box>
				)}

				{source.kind === "invoice" && invoicePayments.length > 1 && (
					<Box sx={{ mt: 2 }}>
						<Typography variant="caption" color="text.secondary">
							{t("common:receipt.pickHint")}
						</Typography>
						<List dense disablePadding>
							{invoicePayments.map((p) => (
								<ListItemButton key={p.id} onClick={() => toggle(p.id)} sx={{ borderRadius: 1, py: 0 }}>
									<ListItemIcon sx={{ minWidth: 34 }}>
										<Checkbox edge="start" size="small" checked={picked.has(p.id)} tabIndex={-1} disableRipple />
									</ListItemIcon>
									<ListItemText
										primary={`${Util.formatDate(p.date, "medium")} · ${p.mode}`}
										primaryTypographyProps={{ variant: "body2" }}
									/>
									<Typography variant="body2" fontWeight={700}>
										{Util.formatNumber(p.amount)}
									</Typography>
								</ListItemButton>
							))}
						</List>
					</Box>
				)}
			</DialogContent>
			<MoleDialogActions>
				<MoleCancelButton onClick={onClose}>{t("common:ui.buttons.cancel")}</MoleCancelButton>
				<MolePrimaryButton startIcon={<PrintOutlined />} onClick={() => run("thermal")} disabled={disabled}>
					{t("common:receipt.print")}
				</MolePrimaryButton>
				<MolePrimaryButton startIcon={<PictureAsPdfOutlined />} onClick={() => run("a4")} disabled={disabled}>
					{t("common:receipt.pdf")}
				</MolePrimaryButton>
			</MoleDialogActions>
		</MoleDialog>
	);
};

/** Paper-like card, light on purpose so a screenshot reads the same in dark mode. */
const ReceiptCard = ({ children }: { children: React.ReactNode }) => (
	<Paper
		variant="outlined"
		sx={{
			p: 2,
			bgcolor: "#fff",
			color: "#111",
			borderStyle: "dashed",
			borderColor: "#bbb",
			fontVariantNumeric: "tabular-nums",
			"& .muted": { color: "#666" },
		}}
	>
		{children}
	</Paper>
);

const CardHeader = ({ preview, title }: { preview: Preview; title: string }) => {
	const { t } = useUI();
	const details = preview.details;
	return (
		<>
			<Box sx={{ textAlign: "center", mb: 1 }}>
				{preview.business && (
					<Typography variant="subtitle2" fontWeight={800}>
						{preview.business}
					</Typography>
				)}
				<Typography variant="caption" sx={{ letterSpacing: 1, textTransform: "uppercase", fontWeight: 700 }}>
					{title}
				</Typography>
			</Box>
			<Line left={t("common:receipt.number")} right={preview.receipt_no} />
			<Line left={t("common:receipt.date")} right={Util.formatDate(preview.date, "medium")} />
			<Line left={t("common:receipt.receivedFrom")} right={<b>{details.contact_name || details.client}</b>} />
			{(details.contact_lines || []).map((line) => (
				<Typography key={line} variant="caption" className="muted" component="div" sx={{ textAlign: "right" }}>
					{line}
				</Typography>
			))}
			<Divider sx={{ my: 1, borderStyle: "dashed", borderColor: "#bbb" }} />
		</>
	);
};

const Line = ({ left, right, strong }: { left: React.ReactNode; right: React.ReactNode; strong?: boolean }) => (
	<Stack direction="row" justifyContent="space-between" spacing={1}>
		<Typography variant="body2" className={strong ? undefined : "muted"} fontWeight={strong ? 800 : 400}>
			{left}
		</Typography>
		<Typography variant="body2" fontWeight={strong ? 800 : 500} sx={{ textAlign: "right" }}>
			{right}
		</Typography>
	</Stack>
);

const PaymentCard = ({ preview }: { preview: PaymentPreview }) => {
	const { t } = useUI();
	const cur = preview.currency;
	return (
		<ReceiptCard>
			<CardHeader preview={preview} title={t("common:receipt.paymentTitle")} />
			{preview.invoices.map((inv) => (
				<Box key={inv.invoice} sx={{ mb: 1 }}>
					<Typography variant="body2" fontWeight={700}>
						{t("common:report.invoiceNo")} {inv.reference}
					</Typography>
					{inv.payments.map((p) => (
						<Line
							key={p.id}
							left={`${Util.formatDate(p.date, "medium")} · ${p.mode}`}
							right={Util.formatNumber(p.amount)}
						/>
					))}
					<Line left={t("common:receipt.invoiceTotal")} right={Util.formatNumber(inv.invoice_total)} />
					<Line left={t("common:receipt.paidToDate")} right={Util.formatNumber(inv.paid_to_date)} />
					{inv.balance > 0 ? (
						<Line left={t("common:report.balanceDue")} right={<b>{Util.formatNumber(inv.balance)}</b>} />
					) : (
						<Line left={t("common:receipt.settled")} right="✓" />
					)}
				</Box>
			))}
			<Divider sx={{ my: 1, borderStyle: "dashed", borderColor: "#bbb" }} />
			<Line strong left={t("common:receipt.amountReceived")} right={money(preview.total_received, cur)} />
			<Typography variant="caption" className="muted" component="div" sx={{ fontStyle: "italic" }}>
				{preview.total_words} {cur}
			</Typography>
			{preview.invoices.length > 1 && preview.total_balance > 0 && (
				<Line left={t("common:report.balanceDue")} right={money(preview.total_balance, cur)} />
			)}
		</ReceiptCard>
	);
};

const WalletCard = ({ preview }: { preview: WalletPreview }) => {
	const { t } = useUI();
	const cur = preview.currency;
	const record = preview.record;
	const tagKey = String(record.tag || "").toLowerCase();
	const known = ["advance", "deposit", "transfer"].includes(tagKey);
	const tagLabel = known ? t(`common:wallet.tags.${tagKey}`) : record.tag;
	return (
		<ReceiptCard>
			<CardHeader preview={preview} title={t("common:receipt.walletTitle")} />
			<Line
				left={t("common:receipt.movement")}
				right={record.source_client_name ? `${tagLabel} (${t("common:receipt.from")} ${record.source_client_name})` : tagLabel}
			/>
			{record.mode && <Line left={t("common:receipt.mode")} right={record.mode} />}
			{record.ref && <Line left={t("common:receipt.reference")} right={record.ref} />}
			{record.description && (
				<Typography variant="caption" className="muted" component="div">
					{record.description}
				</Typography>
			)}
			<Divider sx={{ my: 1, borderStyle: "dashed", borderColor: "#bbb" }} />
			<Line strong left={t("common:receipt.amountReceived")} right={money(preview.amount, cur)} />
			<Typography variant="caption" className="muted" component="div" sx={{ fontStyle: "italic" }}>
				{preview.total_words} {cur}
			</Typography>
			<Line left={t("common:receipt.walletBalance")} right={money(preview.balance, cur)} />
		</ReceiptCard>
	);
};

export default ReceiptDialog;
